import React, { useCallback, useId } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getSortableMoveIndexes } from '@/components/sortable/verticalSortableUtils.js';
import { VIDEO_PRODUCTION_SLOT_KIND } from '../../constants.js';
import {
  buildAssetPickerEntries,
  prioritizeSelectedAssets,
} from '../../utils/productionSlotUtils.js';

const POINTER_ACTIVATION_DISTANCE = 6;
const EMPTY_SELECTED_VALUES = [];

const AssetPreview = ({ asset, index }) => {
  const isVideo = asset.mediaType === VIDEO_PRODUCTION_SLOT_KIND.VIDEO;

  if (isVideo) {
    if (asset.coverUrl) {
      return <img src={asset.coverUrl} alt={asset.label || `视频 ${index + 1}`} />;
    }
    if (asset.url) {
      return <video src={asset.url} muted playsInline preload="metadata" />;
    }
    return <span>无封面</span>;
  }

  return asset.url ? (
    <img src={asset.url} alt={asset.label || `图片 ${index + 1}`} />
  ) : (
    <span>无预览</span>
  );
};

const AssetCard = ({
  asset,
  checked,
  disabled,
  dragHandle,
  index,
  isDragging = false,
  onToggle,
  order,
  selectedIndex,
  setNodeRef,
  style,
}) => {
  const inputId = useId();
  const isDisabled = disabled || !asset.value;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`asset-card ${checked ? 'is-selected' : ''} ${
        isDisabled ? 'is-disabled' : ''
      } ${isDragging ? 'is-dragging' : ''}`}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={isDisabled}
        onChange={() => onToggle(asset, selectedIndex)}
      />
      {checked && order && (
        <span className="asset-card__order" aria-label={`已选顺序 ${order}`}>
          {order}
        </span>
      )}
      {dragHandle}
      <label className="asset-card__content" htmlFor={inputId}>
        <span className="asset-card__media">
          <AssetPreview asset={asset} index={index} />
        </span>
        <span className="asset-card__meta">
          <strong>{asset.label || `素材 ${index + 1}`}</strong>
          <span>{asset.categoryName || '未分类'}</span>
        </span>
      </label>
    </div>
  );
};

const SortableAssetCard = ({
  asset,
  disabled,
  dragId,
  index,
  onToggle,
  order,
  selectedIndex,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dragId,
    disabled,
  });

  return (
    <AssetCard
      asset={asset}
      checked
      disabled={disabled}
      index={index}
      isDragging={isDragging}
      onToggle={onToggle}
      order={order}
      selectedIndex={selectedIndex}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      dragHandle={(
        <button
          type="button"
          className="asset-card__drag-handle"
          disabled={disabled}
          aria-label={`拖动素材 ${order}`}
          {...attributes}
          {...listeners}
        >
          <i className="bi bi-grip-vertical" aria-hidden="true" />
        </button>
      )}
    />
  );
};

const ProductionAssetPicker = ({
  allAssets,
  visibleAssets,
  selectedValue,
  selectedValues = EMPTY_SELECTED_VALUES,
  disabled = false,
  emptyText,
  maxReached = false,
  onMove,
  onToggle,
}) => {
  const normalizedSelectedValues = selectedValues.map(String);
  const pinnedSelectedValues = normalizedSelectedValues.length
    ? normalizedSelectedValues
    : selectedValue
      ? [String(selectedValue)]
      : EMPTY_SELECTED_VALUES;
  const multiple = typeof onMove === 'function';
  const entries = multiple
    ? buildAssetPickerEntries({
        allAssets,
        visibleAssets,
        selectedValues: normalizedSelectedValues,
      })
    : prioritizeSelectedAssets({
        allAssets,
        visibleAssets,
        selectedValues: pinnedSelectedValues,
      }).map((asset, index) => ({
        id: String(asset.id || asset.value || `asset-${index}`),
        asset,
        selectedIndex: null,
        order: null,
      }));
  const sortableItemIds = entries
    .filter((entry) => entry.selectedIndex !== null)
    .map((entry) => entry.id);
  const sortable = sortableItemIds.length > 0;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: POINTER_ACTIVATION_DISTANCE },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (disabled || !over || typeof onMove !== 'function') return;
      const indexes = getSortableMoveIndexes(
        sortableItemIds,
        active.id,
        over.id
      );
      if (indexes) onMove(indexes);
    },
    [disabled, onMove, sortableItemIds]
  );

  if (!entries.length) {
    return <div className="video-production-create-page__empty">{emptyText}</div>;
  }

  const grid = (
    <div className="video-production-create-page__asset-grid">
      {entries.map((entry, index) => {
        const { asset, id, order, selectedIndex } = entry;
        const assetValue = String(asset.value || '');
        const checked = multiple
          ? selectedIndex !== null
          : String(selectedValue || '') === assetValue;
        const selectionDisabled =
          disabled || !asset.value || (!checked && maxReached);

        return sortable && checked ? (
          <SortableAssetCard
            key={id}
            asset={asset}
            disabled={disabled}
            dragId={id}
            index={index}
            onToggle={onToggle}
            order={order}
            selectedIndex={selectedIndex}
          />
        ) : (
          <AssetCard
            key={id}
            asset={asset}
            checked={checked}
            disabled={selectionDisabled}
            index={index}
            onToggle={onToggle}
            order={order}
            selectedIndex={selectedIndex}
          />
        );
      })}
    </div>
  );

  if (!sortable) return grid;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableItemIds}
        strategy={rectSortingStrategy}
      >
        {grid}
      </SortableContext>
    </DndContext>
  );
};

export default ProductionAssetPicker;
